<script setup lang="ts">
/**
 * A dropdown that narrows as you type. It holds the id of the option picked and shows that option's label; typing
 * filters the list, the arrow keys move through it, and Enter or a click picks. Enter never submits a form the
 * field sits in. A stored id the list does not hold (the list failed to load, or holds only its first page) is
 * shown as the id, so what is saved is never hidden.
 */
import { Button, ZhPanel } from '@zerohuman/ui'
import { computed, ref } from 'vue'

/** One thing to pick. `search` is extra text a query may match besides the label, such as a slug. */
export type SearchSelectOption = { id: string; label: string; search?: string }

const props = defineProps<{
    modelValue: string | null
    options: SearchSelectOption[]
    /** What the field says while nothing is picked. */
    placeholder?: string
    /** What the list says when nothing matches. */
    emptyText?: string
    disabled?: boolean
    ariaLabel?: string
}>()
const emit = defineEmits<{ 'update:modelValue': [string] }>()

const query = ref('')
const editing = ref(false)
const open = ref(false)
const activeIndex = ref(0)
/** How many matches to paint. The rest stay one more keystroke away. */
const SHOWN_LIMIT = 40

/** Lower is a closer match. 99 means the option does not match the query at all. */
function score(option: SearchSelectOption, needle: string): number {
    if (!needle) return 0
    const texts = [option.label, option.search ?? ''].map(text => text.toLowerCase())
    if (texts.includes(needle)) return 0
    if (texts.some(text => text.startsWith(needle))) return 1
    if (texts.some(text => text.includes(needle))) return 2
    return 99
}

const matches = computed(() => {
    const needle = query.value.trim().toLowerCase()
    return props.options
        .map((option, index) => ({ option, index, score: score(option, needle) }))
        .filter(row => row.score < 99)
        .sort((a, b) => a.score - b.score || a.index - b.index)
        .map(row => row.option)
})

const shown = computed(() => matches.value.slice(0, SHOWN_LIMIT))

const currentLabel = computed(() => {
    if (!props.modelValue) return ''
    return props.options.find(option => option.id === props.modelValue)?.label ?? props.modelValue
})

function startEdit(): void {
    editing.value = true
    open.value = true
    query.value = ''
    activeIndex.value = 0
}

function onInput(event: Event): void {
    editing.value = true
    open.value = true
    query.value = (event.target as HTMLInputElement).value
    activeIndex.value = 0
}

function close(): void {
    editing.value = false
    open.value = false
    query.value = ''
}

function choose(id: string): void {
    emit('update:modelValue', id)
    close()
}

function chooseHighlighted(): void {
    const pick = open.value ? shown.value[activeIndex.value] : undefined
    if (pick) choose(pick.id)
}

function move(step: number): void {
    open.value = true
    const count = shown.value.length
    if (count === 0) return
    activeIndex.value = (activeIndex.value + step + count) % count
}

function stopEdit(): void {
    // An option is picked on mousedown, which arrives before this blur. Wait so that click still lands.
    window.setTimeout(close, 120)
}
</script>

<template>
	<div class="relative">
		<input
			:value="editing ? query : currentLabel"
			type="text"
			role="combobox"
			aria-autocomplete="list"
			:aria-expanded="open"
			:aria-label="ariaLabel"
			:placeholder="placeholder"
			:disabled="disabled"
			autocomplete="off"
			class="border-input bg-background w-full rounded-md border px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
			@focus="startEdit"
			@input="onInput"
			@keydown.enter.stop.prevent="chooseHighlighted"
			@keydown.down.stop.prevent="move(1)"
			@keydown.up.stop.prevent="move(-1)"
			@keydown.esc="open = false"
			@blur="stopEdit"
		/>
		<ZhPanel
			v-if="open && !disabled"
			as="ul"
			flush
			data-testid="search-select-results"
			role="listbox"
			class="absolute z-20 mt-1 max-h-60 w-full overflow-auto shadow"
		>
			<li v-for="(option, index) in shown" :key="option.id">
				<Button
					type="button"
					:variant="index === activeIndex ? 'secondary' : 'ghost'"
					class="h-auto w-full justify-start py-1 text-left whitespace-normal"
					data-testid="search-select-option"
					role="option"
					:aria-selected="index === activeIndex"
					@mousedown.prevent="choose(option.id)"
				>
					{{ option.label }}
				</Button>
			</li>
			<li v-if="shown.length === 0" class="text-muted-foreground px-2 py-1 text-xs">
				{{ emptyText ?? 'Nothing matches.' }}
			</li>
			<li v-else-if="matches.length > shown.length" class="text-muted-foreground px-2 py-1 text-xs">
				{{ matches.length - shown.length }} more. Keep typing to narrow the list.
			</li>
		</ZhPanel>
	</div>
</template>
